<template>
  <v-card class="mb-6">
    <v-card-title>
      <span class="secondary--text subtitle-2">{{ title }}</span>
    </v-card-title>
    <v-divider />
    <v-data-table no-data-text="Nenhum exercício cadastrado" disable-pagination hide-default-footer
      :single-expand="true" :expanded.sync="expanded" item-key="uniqueId" :headers="tableHeaders" :items="list">
      <template v-slot:body="{ items, isExpanded }">
        <draggable v-model="list" tag="tbody" handle=".drag" ghost-class="ghost" @end="updatePosition">
          <tr v-for="(item) in items" :key="item.uniqueId">
            <td :colspan="headers.length" class="pa-0 ma-0">
              <data-table-row-handler :item="item" :headers="headers" :isExpanded="isExpanded(item)" :canDrag="canDrag"
                :readonly="readonly">
                <template v-slot:item.exercise.name="{ item }">
                  {{ getExerciseName(item) }}
                </template>
                <template v-slot:item.quantidade_tiros="{ item }">
                  {{ getQtdTiros(item) }}
                </template>
                <template v-slot:item.min_reps="{ item }">
                  {{ getMinReps(item) }}
                </template>
                <template v-slot:item.max_reps="{ item }">
                  {{ getMaxReps(item) }}
                </template>
                <template v-slot:item.muscleGroups="{ item }">
                  {{ getMuscleGroups(item) }}
                </template>
                <template v-slot:item.exercise_technic.name="{ item }">
                  {{ getTechniqueName(item) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn class="mr-2" text icon x-small color="secondary" v-if="!readonly"
                    @click="newExerciseDialog(item)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn class="mr-2" text icon x-small color="secondary" v-if="!readonly"
                    @click="destroyExerciseDialog(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-btn class="mr-2" text icon x-small color="secondary" v-if="!readonly"
                    @click="copyExerciseDialog(item)">
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn class="mr-2" text icon x-small color="secondary" v-if="item.exercise_technic"
                    @click.stop="openTechniqueHint(item)">
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                  <v-btn text icon x-small color="secondary" v-if="item.observacao" @click.stop="expandRow(item)">
                    <v-icon>{{ expandIcon(item) }}</v-icon>
                  </v-btn>
                </template>
                <template v-slot:expanded-item v-if="item.observacao">
            <td :colspan="headers.length" class="observations grey lighten-4 grey--text text--darken-2">
              Observações: {{ item.observacao }}
            </td>
      </template>
      </data-table-row-handler>
      </td>
      </tr>
      </draggable>
</template>
</v-data-table>

<v-dialog v-model="techniqueHintDialog" max-width="500">
  <v-card>
    <v-card-title class="headline">{{ techniqueHintTitle }}</v-card-title>
    <v-card-text>{{ techniqueHintDescription }}</v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="primary" text @click="techniqueHintDialog = false">Fechar</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import draggable from 'vuedraggable'
import DataTableRowHandler from './DataTableRowHandler.vue'
import mesocycleService from '@/services/mesocycle-service'
import microcycleService from '@/services/microcycle-service'
import exerciseTechnicsService from '@/services/user-exercise-technics-service'

export default {
  name: 'student-training-table',
  components: {
    draggable,
    DataTableRowHandler
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    headers: {
      type: Array
    },
    items: {
      type: Array
    },
    trainingIndex: {
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    cycle: {
      type: String,
      default: 'meso'
    }
  },
  data () {
    return {
      expanded: [],
      exercises: [...this.items],
      list: this.items.map((item, index) => ({
        ...item,
        order: index + 1,
        uniqueId: this.generateUniqueId(item, index)
      })),
      cycleToBeEvaluated: {
        trainings: []
      },
      techniqueHintDialog: false,
      techniqueHintTitle: '',
      techniqueHintDescription: ''
    }
  },
  methods: {
    ...mapActions('base', ['setLoader']),
    ...mapActions('students', [
      'setTrainingIndex',
      'setCreateOrUpdateExerciseDialog',
      'setCreateOrUpdateExercise',
      'setCreateOrUpdateExerciseIndex'
    ]),
    generateUniqueId (item, index) {
      if (Array.isArray(item.exercise)) {
        return `array-${item.exercise.map(ex => ex.id).join('-')}-${index}`
      }
      return `single-${item.exercise?.id || 'no-id'}-${index}`
    },
    getExerciseName (item) {
      if (Array.isArray(item.exercise)) {
        return item.exercise.map(ex => ex.name || 'Sem nome').join(' / ')
      }
      return item.exercise?.name || 'Sem nome'
    },
    getMinReps (item) {
      if (Array.isArray(item.exercise)) {
        return item.exercise.map(ex => ex.min_reps || '-').join(' / ')
      }
      return item.exercise?.min_reps || item.min_reps || '-'
    },
    getMaxReps (item) {
      if (Array.isArray(item.exercise)) {
        return item.exercise.map(ex => ex.max_reps || '-').join(' / ')
      }
      return item.exercise?.max_reps || item.max_reps || '-'
    },
    getQtdTiros (item) {
      if (Array.isArray(item.exercise)) {
        return item.exercise.map(ex => ex.quantidade_tiros || '-').join(' / ')
      }
      return item.exercise?.quantidade_tiros || item.quantidade_tiros || '-'
    },
    getMuscleGroups (item) {
      if (Array.isArray(item.exercise)) {
        return item.exercise
          .flatMap(ex => ex.muscle_group || [])
          .map(mg => mg.name)
          .filter((name, idx, self) => self.indexOf(name) === idx)
          .join(', ') || '-'
      }
      return (item.exercise?.muscle_group || []).map(mg => mg.name).join(', ') || '-'
    },
    getTechniqueName (item) {
      return item.exercise_technic?.name || '-'
    },
    newExerciseDialog (exercise) {
      this.setTrainingIndex(this.trainingIndex)
      this.setCreateOrUpdateExercise(exercise)
      const exerciseIndex = exercise?.sq || this.cycleToBeEvaluated.trainings[this.trainingIndex].exercises.findIndex(el => {
        const compareArray = (a, b) => a.every((ex, idx) => ex.id === b[idx]?.id)
        const hasSQ = el.sq !== undefined && exercise.sq !== undefined

        if (Array.isArray(el.exercise) && Array.isArray(exercise.exercise)) {
          return hasSQ ? el.sq === exercise.sq && compareArray(el.exercise, exercise.exercise)
            : compareArray(el.exercise, exercise.exercise)
        } else if (!Array.isArray(el.exercise) && !Array.isArray(exercise.exercise)) {
          return hasSQ ? el.sq === exercise.sq && el.exercise.id === exercise.exercise.id
            : el.exercise.id === exercise.exercise.id
        }
        return false
      })
      this.setCreateOrUpdateExerciseIndex(exerciseIndex)
      this.setCreateOrUpdateExerciseDialog(false)
      this.setCreateOrUpdateExerciseDialog(true)
    },
    destroyExerciseDialog (item) {
      this.$emit('destroyExerciseDialog', item)
    },
    copyExerciseDialog (item) {
      this.$emit('copyExerciseDialog', item, this.trainingIndex)
    },
    expandRow (item) {
      const index = this.expanded.findIndex(exp => exp.uniqueId === item.uniqueId)
      if (index > -1) {
        this.expanded.splice(index, 1)
      } else {
        this.expanded = [item]
      }
      this.$forceUpdate()
    },
    expandIcon (item) {
      return this.expanded.find(exp => exp.uniqueId === item.uniqueId) ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },
    async openTechniqueHint (item) {
      if (!item.exercise_technic || !item.exercise_technic.name) {
        this.techniqueHintTitle = 'Técnica'
        this.techniqueHintDescription = 'Sem descrição disponível'
        this.techniqueHintDialog = true
        return
      }

      this.setLoader({ show: true })
      try {
        const response = await exerciseTechnicsService.all({ q: item.exercise_technic.name })
        const techniques = response.data // Array retornado pela API
        const technique = techniques.exercise_technics[0]
        this.techniqueHintTitle = technique?.name || item.exercise_technic.name
        this.techniqueHintDescription = technique?.description || 'Sem descrição disponível'
      } catch (error) {
        console.error('Erro ao buscar técnica:', error)
        this.techniqueHintTitle = item.exercise_technic.name
        this.techniqueHintDescription = 'Descrição não encontrada!'
      } finally {
        this.setLoader({ show: false })
        this.techniqueHintDialog = true
      }
    },
    deepCloneExercise (exercise) {
      // Faz deep copy do exercício para evitar referências compartilhadas
      if (!exercise) return null
      return JSON.parse(JSON.stringify(exercise))
    },
    generateExerciseKey (exercise) {
      // Gera uma chave única baseada em sq e IDs dos exercícios
      const exerciseIds = Array.isArray(exercise.exercise)
        ? exercise.exercise.map(ex => ex.id).sort().join('-')
        : exercise.exercise?.id || 'no-id'
      const sq = exercise.sq !== undefined && exercise.sq !== null ? exercise.sq : 'no-sq'
      return `${sq}-${exerciseIds}`
    },
    getExerciseType (exercise) {
      // Retorna o tipo do exercício
      if (Array.isArray(exercise.exercise)) {
        return exercise.exercise[0]?.exercise_type
      }
      return exercise.exercise?.exercise_type
    },
    updatePosition () {
      this.list.forEach((exercise, index) => {
        exercise.order = index + 1
      })
      this.exercises = [...this.list]

      // Identifica o tipo de exercício desta tabela (baseado no primeiro exercício do list)
      const currentExerciseType = this.list.length > 0 ? this.getExerciseType(this.list[0]) : null

      // Obtém TODOS os exercícios do treino original
      const allOriginalExercises = this.cycleToBeEvaluated.trainings[this.trainingIndex].exercises

      // Separa os exercícios: os do tipo atual e os de outros tipos
      const exercisesOfCurrentType = []
      const exercisesOfOtherTypes = []

      allOriginalExercises.forEach((exercise) => {
        const exerciseType = this.getExerciseType(exercise)
        if (exerciseType === currentExerciseType) {
          exercisesOfCurrentType.push(exercise)
        } else {
          exercisesOfOtherTypes.push(this.deepCloneExercise(exercise))
        }
      })

      // Cria um mapa dos exercícios do tipo atual usando chave única composta
      const currentTypeExercisesMap = new Map()
      exercisesOfCurrentType.forEach((exercise) => {
        const key = this.generateExerciseKey(exercise)
        currentTypeExercisesMap.set(key, this.deepCloneExercise(exercise))
      })

      // Atualiza os exercícios do tipo atual baseado na ordem do list
      const updatedCurrentTypeExercises = this.list.map((listExercise, index) => {
        // Busca o exercício original pelo sq primeiro (mais confiável)
        let originalExercise = null

        if (listExercise.sq !== undefined && listExercise.sq !== null) {
          // Tenta encontrar pelo sq primeiro
          for (const [, ex] of currentTypeExercisesMap.entries()) {
            if (ex.sq === listExercise.sq) {
              originalExercise = ex
              break
            }
          }
        }

        // Se não encontrou pelo sq, tenta pela chave completa
        if (!originalExercise) {
          const listKey = this.generateExerciseKey(listExercise)
          if (currentTypeExercisesMap.has(listKey)) {
            originalExercise = currentTypeExercisesMap.get(listKey)
          }
        }

        // Se ainda não encontrou, tenta pelos IDs dos exercícios
        if (!originalExercise) {
          const listExerciseIds = Array.isArray(listExercise.exercise)
            ? listExercise.exercise.map(ex => ex.id).sort()
            : [listExercise.exercise?.id].filter(Boolean)

          for (const [, ex] of currentTypeExercisesMap.entries()) {
            const exIds = Array.isArray(ex.exercise)
              ? ex.exercise.map(e => e.id).sort()
              : [ex.exercise?.id].filter(Boolean)

            // Compara arrays de IDs
            const idsMatch = listExerciseIds.length === exIds.length &&
              listExerciseIds.length > 0 &&
              listExerciseIds.every((id, idx) => id === exIds[idx])

            if (idsMatch) {
              originalExercise = ex
              break
            }
          }
        }

        // Se encontrou o exercício original, usa ele com deep copy e atualiza apenas a ordem
        if (originalExercise) {
          const cloned = this.deepCloneExercise(originalExercise)
          cloned.order = index + 1
          // Remove da map para evitar duplicatas
          currentTypeExercisesMap.delete(this.generateExerciseKey(originalExercise))
          return cloned
        }

        // Se não encontrou, usa o exercício do list com deep copy
        const cloned = this.deepCloneExercise(listExercise)
        cloned.order = index + 1
        return cloned
      })

      // Combina os exercícios atualizados do tipo atual com os exercícios de outros tipos
      const allUpdatedExercises = [...updatedCurrentTypeExercises, ...exercisesOfOtherTypes]

      // Ordena todos os exercícios pela ordem
      allUpdatedExercises.sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))

      this.$set(this.cycleToBeEvaluated.trainings[this.trainingIndex], 'exercises', allUpdatedExercises)

      this.setLoader({ show: true })
      const payload = { ...this.cycleToBeEvaluated }

      if (this.cycleType === 'micro') {
        microcycleService
          .createOrUpdate(payload.macrocycle.student_id, payload.mesocycle.macrocycle_id, payload.mesocycle.id, payload)
          .finally(() => this.setLoader({ show: false }))
      } else {
        mesocycleService
          .createOrUpdate(payload.macrocycle.student_id, payload.macrocycle.id, payload)
          .finally(() => this.setLoader({ show: false }))
      }
    },
    setCycle () {
      this.cycleToBeEvaluated = ['micro'].includes(this.cycle, this.cycleType) ? this.microcycleToBeEvaluated : this.mesocycleToBeEvaluated
    }
  },
  computed: {
    ...mapGetters('students', ['mesocycleToBeEvaluated', 'cycleType', 'microcycleToBeEvaluated']),
    tableHeaders () {
      return this.readonly ? this.headers.slice(1, this.headers.length) : this.headers
    },
    canDrag () {
      return this.items.length > 1
    }
  },
  watch: {
    items (newVal) {
      this.expanded = []
      this.exercises = [...newVal]
      this.list = newVal.map((item, index) => ({
        ...item,
        order: index + 1,
        uniqueId: this.generateUniqueId(item, index)
      }))
    }
  },
  mounted () {
    this.setCycle()
  }
}
</script>

<style scoped>
.observations {
  padding: 8px 66px;
  word-break: break-word;
}

.ghost {
  opacity: 0.5;
  background: #f5f5f5;
}
</style>
